import React, { useEffect } from "react";

import { Box, Button, Grid, InputAdornment, TextField } from "@mui/material";
import { Controller } from "react-hook-form";
import * as yup from "yup";

import useFormWithYup from "@/hooks/useFormWithYup";
import { muiField } from "@/utils/hookForm";

interface Props {
  values?: TemplateDetailsFormValues;
  submitLabel?: string;
  onSubmit(values: TemplateDetailsFormValues, isDirty: boolean): void;
}

const validationSchema = yup
  .object({
    name: yup.string().label("Title").required(),
    description: yup.string().label("Description").notRequired().presence(),
    durationDays: yup
      .number()
      .label("Duration")
      .presence()
      .required()
      .min(1, "${label} must be more than 0 days"),
    recommendedCalories: yup
      .number()
      .label("Recommended calories")
      .presence()
      .required()
      .min(1, "${label} must be more than 0"),
  })
  .defined();

export type TemplateDetailsFormValues = yup.InferType<typeof validationSchema>;

const TemplateDetailsForm: React.FC<Props> = ({
  values,
  submitLabel,
  onSubmit,
}) => {
  const { control, reset, formState, handleSubmit } = useFormWithYup({
    defaultValues: {
      name: "",
      description: "",
      durationDays: "",
      recommendedCalories: "",
    },
    validationSchema,
  });

  useEffect(() => {
    if (values) {
      reset(
        {
          name: values.name,
          description: values.description,
          durationDays: values.durationDays.toString(),
          recommendedCalories: values.recommendedCalories.toString(),
        },
        { keepTouched: true }
      );
    }
  }, [values]);

  const _onSubmit = (values: TemplateDetailsFormValues) => {
    onSubmit(values, formState.isDirty);
  };

  formState.isDirty;

  return (
    <form onSubmit={handleSubmit(_onSubmit)}>
      <Grid container spacing={2}>
        <Grid container item xs={12}>
          <Grid item xs={4}>
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState }) => {
                return (
                  <TextField
                    {...muiField(field)}
                    label="Title"
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message ?? " "}
                    fullWidth
                  />
                );
              }}
            />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={4}>
            <Controller
              control={control}
              name="durationDays"
              render={({ field, fieldState }) => (
                <TextField
                  {...muiField(field, { maxDecimalPlace: 0 })}
                  label="Duration"
                  type="number"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">days</InputAdornment>
                    ),
                  }}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? " "}
                  fullWidth
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={8}>
            <Controller
              control={control}
              name="description"
              render={({ field, fieldState }) => (
                <TextField
                  {...muiField(field)}
                  label="Description"
                  placeholder="Write description here..."
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? " "}
                  multiline
                  rows={4}
                  fullWidth
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid container item xs={12}>
          <Grid item xs={8}>
            <Controller
              control={control}
              name="recommendedCalories"
              render={({ field, fieldState }) => (
                <TextField
                  {...muiField(field, { maxDecimalPlace: 0 })}
                  label="Recommended calories"
                  type="number"
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message ?? " "}
                  fullWidth
                />
              )}
            />
          </Grid>
        </Grid>
        <Grid item xs={8}>
          <Box display="flex" justifyContent="center">
            <Button type="submit">{submitLabel}</Button>
          </Box>
        </Grid>
      </Grid>
    </form>
  );
};

export default TemplateDetailsForm;
